import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import {
    Body1,
    Button,
    Image,
    makeStyles,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    mergeClasses,
    tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseFilled, ArrowClockwiseRegular, bundleIcon } from '@fluentui/react-icons'
import { DateTime } from 'luxon'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from '../../context/AuthorizationContext'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingVerticalM,
    },
    containerXXL: {
        width: '70%',
        alignSelf: 'center',
    },
    logo: {
        maxHeight: '100px',
    },
})

const ArrowClockwise = bundleIcon(ArrowClockwiseFilled, ArrowClockwiseRegular)

/**
 * Outlook keeps refusing to hand out a token because we asked too often (Office error 13013), and
 * the client has stopped asking by itself. A third dead end alongside the two in `SelectHost`: a
 * spinner here would never resolve, so say what is happening and offer the one thing that helps.
 *
 * The button is not decoration. A throttle lifts on its own, but a deliberate click is the signal
 * Office' rate limiting is built to admit, where a retry timer is exactly what caused this.
 */
export default function BootstrapThrottled(): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { bootstrapBlocked, retryBootstrap } = useContext(AuthorizationContext)

    const containerXXL = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])

    // Rounded up and floored at one: "over 0 minuten" reads as broken, and the exact second is
    // neither knowable nor useful.
    const minutes = useMemo<number>(() => {
        const remainingMs = bootstrapBlocked ? bootstrapBlocked.retryAt.diff(DateTime.now().toUTC()).toMillis() : 0
        return Math.max(1, Math.ceil(remainingMs / 60_000))
    }, [bootstrapBlocked])

    return (
        <div className={mergeClasses(styles.container, containerXXL && styles.containerXXL)}>
            <Image src={'images/hammock.svg'} className={styles.logo} />

            <MessageBar intent={'warning'} layout={'multiline'}>
                <MessageBarBody>
                    <MessageBarTitle>{translate('authentication.bootstrap.throttled.title')}</MessageBarTitle>{' '}
                    {translate('authentication.bootstrap.throttled.explanation', { minutes })}
                </MessageBarBody>
                <MessageBarActions>
                    <Button appearance={'primary'} icon={<ArrowClockwise />} onClick={() => retryBootstrap()}>
                        {translate('authentication.bootstrap.throttled.retry')}
                    </Button>
                </MessageBarActions>
            </MessageBar>

            <Body1>{translate('authentication.bootstrap.throttled.advice')}</Body1>
        </div>
    )
}
